import * as React from "react";
import { Box, CircularProgress, Typography, useColorScheme } from "@mui/material";

import apiClient from "@/api-client";
import { Conversation } from "@/hooks/api/use-call-logs";

import { MessageBubble, MessagesPlaceholder } from "./message-bubble";

interface ConversationTranscriptProps {
	/**
	 * Typed loosely because the two callers disagree: a call log carries it as a string and a
	 * Proving Ground cell as a number. Both are the same integer id, and correcting either
	 * type ripples through pages that have nothing to do with this component.
	 */
	conversationId: string | number | null;
	/** Playhead in seconds, used to light up the turn currently being spoken. */
	currentTime: number;
	currentTimeRef: React.MutableRefObject<number>;
	/** False tears the transcript down — a closed modal or drawer should not hold a request open. */
	active: boolean;
	emptyMessage?: string;
	/**
	 * English renderings keyed by turn id. When supplied, each turn shows its translation in
	 * place of the original — the original stays the record, this is only how it is read.
	 */
	translation?: Record<string, string>;
}

/**
 * A conversation, read as it was spoken.
 *
 * This is the whole transcript pane from the call log modal, lifted out so a simulated call
 * and a real one are read through the same component rather than two that look alike until
 * one of them is changed.
 */
export function ConversationTranscript({
	conversationId,
	currentTime,
	currentTimeRef,
	active,
	emptyMessage = "No conversations found for this call log",
	translation,
}: ConversationTranscriptProps): React.JSX.Element {
	const [messages, setMessages] = React.useState<Conversation[]>([]);
	const [isLoading, setIsLoading] = React.useState(false);
	const [hasMore, setHasMore] = React.useState(true);
	const [page, setPage] = React.useState(1);

	const { colorScheme = "light" } = useColorScheme();

	const messagesContainerRef = React.useRef<HTMLDivElement>(null);
	const abortRef = React.useRef<AbortController | null>(null);
	const requestIdRef = React.useRef(0);

	function resetState() {
		setMessages([]);
		setIsLoading(false);
		setHasMore(true);
		setPage(1);
	}

	function dedupeAppend(previous: Conversation[], nextChunk: Conversation[]): Conversation[] {
		if (previous.length === 0) return nextChunk;
		const byId = new Map<string, Conversation>();
		for (const m of previous) byId.set(m.id, m);
		for (const m of nextChunk) byId.set(m.id, m);
		return [...byId.values()];
	}

	const fetchMessages = async (pageNum: number) => {
		if (!conversationId) return;

		if (abortRef.current) {
			abortRef.current.abort();
		}
		const controller = new AbortController();
		abortRef.current = controller;
		const currentRequestId = ++requestIdRef.current;

		setIsLoading(true);
		try {
			setPage(pageNum);
			const limit = 20;
			const offset = (pageNum - 1) * limit;
			const response = await apiClient.get(`/conversations/${conversationId}/entries?offset=${offset}&limit=${limit}`, {
				signal: controller.signal,
			});

			if (currentRequestId !== requestIdRef.current) return;

			const next = response.data.data as Conversation[];
			const total = response.data.total as number;

			setMessages((prev) => dedupeAppend(prev, next));
			setHasMore((prevHasMore) => prevHasMore && pageNum * limit < total);
		} catch (error: any) {
			if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
				return;
			}
			console.error(error);
		} finally {
			if (currentRequestId === requestIdRef.current) setIsLoading(false);
		}
	};

	React.useEffect(() => {
		if (abortRef.current) abortRef.current.abort();
		resetState();
		if (active && conversationId) fetchMessages(1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active, conversationId]);

	return (
		<Box
			ref={messagesContainerRef}
			sx={{
				flex: 1,
				overflowY: "auto",
				p: 2,
				display: "flex",
				flexDirection: "column",
				gap: 1,
				height: "100%",
			}}
			onScroll={(event: React.UIEvent<HTMLDivElement>) => {
				const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
				const isNearBottom = scrollTop + clientHeight >= scrollHeight - 60;

				if (isNearBottom && hasMore && !isLoading) {
					fetchMessages(page + 1);
				}
			}}
		>
			{isLoading && messages.length === 0 && (
				<>
					<MessagesPlaceholder messageWidth={220} />
					<MessagesPlaceholder isAgent messageWidth={350} />
					<MessagesPlaceholder messageWidth={300} />
				</>
			)}

			{!isLoading && messages.length === 0 && (
				<Box sx={{ textAlign: "center", flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
					<Typography color="text.secondary" variant="h4">
						{emptyMessage}
					</Typography>
				</Box>
			)}

			{messages.map((message) => (
				<MessageBubble
					key={message?.id}
					message={
						translation?.[String(message.id)]
							? // Completion cleared with it: that field drives a diff against what speech
								// synthesis was handed, and diffing a translation against the original
								// highlights the whole turn as changed.
								({ ...message, content: translation[String(message.id)], completion: "" } as Conversation)
							: message
					}
					colorScheme={colorScheme}
					currentTime={currentTime}
					currentTimeRef={currentTimeRef}
				/>
			))}

			{isLoading && messages.length > 0 && (
				<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
					<CircularProgress size={20} />
				</Box>
			)}
		</Box>
	);
}
